import _ from 'underscore';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { getEntityUrl } from '@culture/common-helpers/cultureUtils';
import { statusesHash } from '@culture/utils/helpers/statuses';

import Breadcrumbs from 'components/Breadcrumbs';
import Container from 'components/Container';
import DetailEntityPreviewLabels from 'components/DetailEntityPreviewLabels';
import FavoritesButtonContainer from 'containers/FavoritesButton';
import Place from 'components/Place';
import PlayButton from 'components/PlayButton';
import VideoContainer from 'components/VideoContainer';

import CommonPropTypes from 'utils/propTypes/common';
import {
	DISCLAIMERS_HASH,
	MISSING_VIDEO_STUB_TEXT,
	getSlideText,
	isYoutubePlayer,
	makeMovieSlides
} from 'utils/helpers/movies';

import CommonDisclaimer from 'components/CommonDisclaimer';
import styles from './styles.module.scss';

const MoviePreview = ({ breadcrumbs, data, theme }) => {
	const disclaimerRef = useRef(null);
	const actionsRef = useRef(null);
	const {
		_id,
		ageRestriction,
		title,
		type,
		materials,
		genres,
		year,
		duration,
		countries,
		institute,
		tifloComments,
		withVideo,
		playerType
	} = data;

	const hasVideo = Boolean(withVideo);

	const showYouTubeDisclaimer = isYoutubePlayer({
		materials,
		playerType,
		withVideo
	});

	const movieUrl = getEntityUrl('movies', data);
	const slides = makeMovieSlides(materials, data.type);
	const slideText = getSlideText(slides);
	const instituteUrl =
		data.institute?.status === statusesHash.published
			? getEntityUrl('institutes', institute)
			: null;

	const movieDuration = duration && `${Math.ceil(duration / 60)} мин`;

	let movieAttributes = [year, movieDuration];

	if (!_.isEmpty(genres)) {
		movieAttributes = [genres[0].title, ...movieAttributes];
	}

	if (!_.isEmpty(countries)) {
		movieAttributes = [
			...movieAttributes,
			_(countries).pluck('title').join(', ')
		];
	}

	movieAttributes = _(movieAttributes).compact();

	// eslint-disable-next-line consistent-return
	useEffect(() => {
		if (disclaimerRef?.current) {
			const disclaimerResizeObserver = new ResizeObserver((entries) => {
				// eslint-disable-next-line no-restricted-syntax
				for (const entry of entries) {
					const { borderBoxSize } = entry;
					const height = borderBoxSize[0].blockSize;

					actionsRef.current.style.setProperty(
						'--disclaimer-offset',
						`${height}px`
					);
				}
			});
			const currentCopy = disclaimerRef.current;
			disclaimerResizeObserver.observe(currentCopy);

			return () => {
				disclaimerResizeObserver.disconnect(currentCopy);
			};
		}
	}, []);

	return (
		<VideoContainer
			{...data}
			theme={theme}
			withDisclaimer={showYouTubeDisclaimer}
			slides={slides}
		>
			{(onPlayButtonClick, slide) => (
				<Container
					className={cx(styles.preview, {
						[styles.preview_withDisclaimer]: showYouTubeDisclaimer
					})}
					innerClassName={styles.previewInner}
				>
					<div className={styles.breadcrumbs}>
						<Breadcrumbs items={breadcrumbs} theme="white" />
					</div>
					<DetailEntityPreviewLabels
						ageRestriction={ageRestriction}
						tifloComments={tifloComments}
					/>
					<h1 className={styles.title}>{title}</h1>
					<div className={styles.row}>
						{!_.isEmpty(movieAttributes) && (
							<div className={styles.list}>
								{movieAttributes.map((attribute) => (
									<div key={attribute} className={styles.listItem}>
										{attribute}
									</div>
								))}
							</div>
						)}
						{!hasVideo && (
							<div className={styles.text}>{MISSING_VIDEO_STUB_TEXT}</div>
						)}
						{slideText && <div className={styles.slideText}>{slideText}</div>}
						<div className={styles.actions} ref={actionsRef}>
							<FavoritesButtonContainer
								title={title}
								entityId={_id}
								entityType="movies"
								type={type}
							/>
						</div>
					</div>
					{hasVideo && (
						<div className={styles.more} data-player="start">
							<PlayButton
								key={movieUrl}
								onClick={onPlayButtonClick}
								text={slide && slide.title}
							/>
						</div>
					)}
					{institute && (
						<Place
							className={styles.row}
							size="medium"
							title={institute.title}
							url={instituteUrl}
						/>
					)}
					{showYouTubeDisclaimer && (
						<div ref={disclaimerRef}>
							<CommonDisclaimer
								className={styles.disclaimer}
								text={DISCLAIMERS_HASH.youtube}
							/>
						</div>
					)}
				</Container>
			)}
		</VideoContainer>
	);
};

MoviePreview.propTypes = {
	data: CommonPropTypes.entity.isRequired,
	breadcrumbs: CommonPropTypes.breadcrumbs.isRequired,
	theme: PropTypes.oneOf(['entity'])
};

export default MoviePreview;
